// "Añadir oficina" — the pure half of the Oficinas screen's new office form.
//
// What the form says about what was typed (the municipality a DANE code names, each field's
// problem) and what `createCierreOffice` is sent. The backend checks all of it again and writes the
// office as an entry of the client's registry would: `dane`, `departamento`, `acceso` and `cupo` in
// its `external_ids`, the name as the matrix writes it. The GLORY part is `cierreGlory.js`'s.
import datos from "../data/municipiosColombia.json";
import { correosEscritos } from "./cierreConsolidado";
import { cambioDeGlory, motivoValido } from "./cierreGlory";
import { normalizarLugar } from "./ubicacionOficinas";

const MUNICIPIOS = datos.municipios;
const CODIGO_OFICINA = /^[A-Za-z0-9][A-Za-z0-9._-]{0,63}$/;

export const ACCESOS = Object.freeze([
  { valor: "URBANA", etiqueta: "Urbana" },
  { valor: "AEREA", etiqueta: "Aérea" },
]);

export const FORMULARIO_VACIO = Object.freeze({
  officeCode: "",
  name: "",
  // What is typed in the municipality search ("sotav…", or the code itself), and the DANE code it
  // settled on — empty until it names exactly one municipality.
  municipio: "",
  dane: "",
  region: "",
  acceso: "",
  cupo: "",
  correos: "",
  gloryEncendida: true,
  reasonCode: "",
  note: "",
});

/** The municipality a DANE code names: `{ municipio, departamento }`, or null when DIVIPOLA has none. */
export function municipioDeDane(dane) {
  const codigo = String(dane || "").trim();
  if (!/^\d{5}$/.test(codigo) || !MUNICIPIOS[codigo]) return null;
  const [, , municipio, departamento] = MUNICIPIOS[codigo];
  return { municipio, departamento };
}

let opciones = null;

/**
 * Every DIVIPOLA municipality as the search offers it: "SAN ANDRÉS DE SOTAVENTO, CÓRDOBA · 23670",
 * by name. Built once.
 */
export function opcionesDeMunicipio() {
  if (!opciones) {
    opciones = Object.entries(MUNICIPIOS)
      .map(([codigo, [, , municipio, departamento]]) => ({
        codigo,
        etiqueta: `${municipio}, ${departamento} · ${codigo}`,
      }))
      .sort((a, b) => a.etiqueta.localeCompare(b.etiqueta, "es"));
  }
  return opciones;
}

/**
 * The municipalities a search names, best first: those whose name starts with what was typed, then
 * those that contain it anywhere (name, department or code). Accents and case do not count.
 */
export function buscarMunicipios(texto, limite = 8) {
  const clave = normalizarLugar(texto);
  if (!clave) return [];
  const empiezan = [];
  const contienen = [];
  opcionesDeMunicipio().forEach((o) => {
    const etiqueta = normalizarLugar(o.etiqueta);
    if (etiqueta.startsWith(clave)) empiezan.push(o);
    else if (etiqueta.includes(clave)) contienen.push(o);
  });
  return [...empiezan, ...contienen].slice(0, limite);
}

/**
 * The DANE code what was typed in the search settles on: an option picked from the list (it ends
 * in its code), or the five digits typed straight away. "" while it names no municipality yet.
 */
export function daneDeMunicipio(texto) {
  const valor = String(texto || "").trim();
  const codigo = /^\d{5}$/.test(valor) ? valor : (valor.match(/· (\d{5})$/) || [])[1];
  return codigo && MUNICIPIOS[codigo] ? codigo : "";
}

/** Only the digits of a typed amount: "300.000.000" is 300000000. */
export const cupoEnDigitos = (texto) => String(texto || "").replace(/\D/g, "");

/** The cupo as it is shown while typed: pesos with dots every three digits, "300.000.000". */
export function cupoConFormato(texto) {
  const digitos = cupoEnDigitos(texto).replace(/^0+(?=\d)/, "");
  return digitos.replace(/\B(?=(\d{3})+(?!\d))/g, ".");
}

/**
 * Each field's problem, in words, keyed by field — empty when the form may be sent.
 * `codigosExistentes` are the client's office codes already on the screen.
 */
export function erroresNuevaOficina(form, codigosExistentes = []) {
  const errores = {};
  const codigo = form.officeCode.trim();
  if (!codigo) errores.officeCode = "Escribe el código de la oficina.";
  else if (!CODIGO_OFICINA.test(codigo)) errores.officeCode = "Solo letras, números, punto, guion o guion bajo.";
  else if (codigosExistentes.map(String).includes(codigo)) errores.officeCode = `La oficina ${codigo} ya existe.`;

  if (!form.name.trim()) errores.name = "Escribe el nombre de la oficina.";

  if (!municipioDeDane(form.dane)) {
    errores.dane = String(form.municipio || "").trim()
      ? "Elige el municipio de la lista, o escribe su código DANE de 5 dígitos."
      : "Busca el municipio de la oficina.";
  }

  if (!form.region.trim()) errores.region = "Elige la regional.";

  const { invalidos } = correosEscritos(form.correos);
  if (invalidos.length) errores.correos = `No es un correo válido: ${invalidos.join(", ")}`;

  if (!form.gloryEncendida && !motivoValido({ reasonCode: form.reasonCode, note: form.note })) {
    errores.glory = form.reasonCode === "OTHER" ? "Escribe por qué no tiene GLORY." : "Elige por qué no tiene GLORY.";
  }
  return errores;
}

/** The name as the registry writes it: upper case, without accents. */
export const nombreDeMatriz = (texto) => normalizarLugar(texto);

/** What `createCierreOffice` is sent. `conGlory` false for a client without the switch. */
export function oficinaParaCrear(form, { conGlory = true } = {}) {
  const lugar = municipioDeDane(form.dane);
  const cupo = cupoEnDigitos(form.cupo);
  return {
    officeCode: form.officeCode.trim(),
    name: form.name.trim(),
    dane: form.dane.trim(),
    region: form.region.trim(),
    ...(lugar ? { departamento: nombreDeMatriz(lugar.departamento) } : {}),
    ...(form.acceso ? { acceso: form.acceso } : {}),
    ...(cupo ? { cupo } : {}),
    emails: correosEscritos(form.correos).correos,
    ...(conGlory
      ? { glory: cambioDeGlory({ encender: form.gloryEncendida, reasonCode: form.reasonCode, note: form.note }) }
      : {}),
  };
}

/** The line the screen says once the office is created. */
export function avisoDeCreacion(data) {
  const oficina = data?.office || {};
  const nombre = oficina.officeName ? `${oficina.officeName} (${oficina.officeCode})` : oficina.officeCode || "La oficina";
  const correos = data?.emailsSaved === false ? " No se pudieron guardar sus correos: agrégalos desde «Correos»." : "";
  return `Oficina ${nombre} creada. Se lee desde su próximo cierre.${correos}`;
}
